import React, { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchChatMessages } from '../../store/slices/chatSlice';
import ChatMessageItem from './ChatMessageItem';
import LoadingSpinner from '../UI/LoadingSpinner';

interface ChatMessagesProps {
  projectId: string;
  onReply?: (message: any) => void;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({ projectId, onReply }) => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector((state) => state.chat.byProject[projectId]?.messages || []);
  const isLoading = useAppSelector((state) => state.chat.byProject[projectId]?.isLoading || false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchChatMessages({ projectId }));
  }, [dispatch, projectId]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="md" text="Loading messages..." />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-8 text-[var(--color-subtext-color)]">
        <p>No messages yet. Start the conversation!</p>
      </div>
    );
  }

  return (
    <div ref={messagesContainerRef} className="space-y-3 overflow-y-auto flex-1">
      {messages.map((message) => (
        <ChatMessageItem
          key={message.id || message._id}
          message={message}
          projectId={projectId}
          onReply={onReply}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;
